import React, { Fragment, useContext, useEffect } from "react";
import { Box } from "@material-ui/core";
import { ProcessContext } from "../../../contexts/ProcessContext";
import ModuleRenderer from "../../modules";
import { FLOW_MESSAGES } from "../components/flowMessages/components";
import AppHeader from "../components/AppHeader";
import ResponsiveContainer from "../components/ResponsiveContainer";
import FlowLoadingWindowManager from "./FlowLoadingWindowManager";
import FlowMessagesWindowManager from "./FlowMessagesWindowManager";
import { AuthContext } from "../../../contexts/AuthContext";
import { oKBehaviours } from "../../../config/config";
import { useEventDispatcher } from "../../../utils/hooks/useEventDispatcher";
import { useLocation, useNavigate } from "react-router-dom";
import { getFlowCollection } from "../../../config/flows/flowFactory/getFlowCollection";
import { HeaderButtons } from "../components/HeaderButtons";
import CaixaBankCardFooter from "../components/CaixaBankCardFooter";

export const SUCCESS_PATH = "success";

function FluxCapacitor(props) {
  const { path } = props;
  const {
    state: processState,
    state: {
      flowItem,
      sectionsArray,
      currentSection,
      currentSectionProgress,
      lastFlowItemOK,
      flowEnded,
      flowItemIsKO,
      currentFlow,
      flowNeedsCheck,
      conditionsToBeInitialModuleInFlowChecked,
      appStarted,
    },
    dispatch,
  } = useContext(ProcessContext);
  const { state: authState } = useContext(AuthContext);
  const [currentModuleObj, setCurrentModuleObj] = React.useState({
    currentModuleObj: undefined,
  });

  const location = useLocation();
  const navigate = useNavigate();
  const successPathName = `${path}/${SUCCESS_PATH}`;

  useEffect(() => {
    selectAndSaveFlow();
  }, []);

  useEffect(() => {
    changeSectionByModuleObjIfNeeded();
    resetFlowMessage();
  }, [currentModuleObj]);

  useEffect(() => {
    getSectionsFromFlowIfNeeded();
  }, [currentSection]);

  useEffect(() => {
    sendOnOKEventsByPreviousModuleObjIfNeeded();
    showOKMessageByPreviousModuleObjIfNeeded();
  }, [lastFlowItemOK]);

  useEffect(() => {
    if (flowEnded && processState.appStarted) endFlow();
  }, [flowEnded, processState.appStarted]);

  useEffect(() => {
    if (currentFlow === undefined && flowNeedsCheck) {
      selectAndSaveFlow();
      return;
    }
    if (currentFlow) {
      if (!conditionsToBeInitialModuleInFlowChecked) {
        checkArrivalConditionsAndGoToFlowItem();
      }
      calculateCurrentModuleObject();
    }
  }, [currentFlow, flowNeedsCheck, flowItem, flowItemIsKO]);

  const calculateCurrentModuleObject = () => {
    const newCurrentModuleObj = flowItemIsKO
      ? currentFlow && currentFlow.modules[flowItem].onKO
      : currentFlow && currentFlow.modules[flowItem];
    if (newCurrentModuleObj !== currentModuleObj) {
      resetScreenPath();
      setCurrentModuleObj(newCurrentModuleObj);
      dispatch({
        type: "SET_CURRENT_MODULE_OBJECT",
        payload: newCurrentModuleObj,
      });
      resetStepProgressInModule();
    }
    const isNewModuleObjOutOfFlow =
      currentFlow && currentModuleObj && !newCurrentModuleObj;
    if (isNewModuleObjOutOfFlow) {
      dispatch({
        type: "RESET_FLOW",
      });
    }
  };

  const resetStepProgressInModule = () => {
    dispatch({
      type: "UPDATE_PROCESS_CONTEXT",
      payload: {
        stepProgressInModule: [],
      },
    });
  };

  const endFlow = () => {
    redirectToSuccessURLIfNotIn();
    dispatch({
      type: "UPDATE_PROCESS_CONTEXT",
      payload: {
        hideSections: true,
      },
    });
  };

  const redirectToSuccessURLIfNotIn = () => {
    if (location.pathname !== successPathName && processState.appStarted) {
      let redirectUrl = successPathName;
      if (location.search) {
        const urlParams = new URLSearchParams(window.location.search);
        redirectUrl = `${successPathName}?${urlParams.toString()}`;
      }
      navigate(redirectUrl);
    }
  };

  const checkArrivalConditionsAndGoToFlowItem = () => {
    const initialFlowItem = getModuleIndexToResume();
    dispatch({
      type: "UPDATE_PROCESS_CONTEXT",
      payload: {
        flowItem: initialFlowItem,
        flowLoading: { show: false },
        conditionsToBeInitialModuleInFlowChecked: true,
      },
    });
    if (!appStarted) {
      const welcomeMessage =
        currentFlow.modules[initialFlowItem].onAppResume?.welcomeMessageId;
      if (welcomeMessage) {
        dispatch({
          type: "SET_FLOW_MESSAGE",
          payload: {
            id: currentFlow.modules[initialFlowItem].onAppResume
              ?.welcomeMessageId,
            fadeIn: true,
          },
        });
      } else {
        dispatch({
          type: "SET_FLOW_MESSAGE",
          payload: { id: FLOW_MESSAGES.APP_START.id, fadeIn: true },
        });
      }
    }
  };

  const getModuleIndexToResume = () => {
    if (currentFlow) {
      for (let i = 0; i < currentFlow.modules.length; i += 1) {
        const moduleArrivalConditions =
          currentFlow.modules[i].onAppResume?.conditionsToBeInitialModuleInFlow;
        if (
          moduleArrivalConditions &&
          moduleArrivalConditions(processState, authState)
        ) {
          return i;
        }
      }
    }
    return 0;
  };

  const getSectionsFromFlowIfNeeded = () => {
    if (currentFlow && currentSection && !sectionsArray) {
      const sections = [];
      currentFlow.modules.forEach((module) => {
        const lastSection = sections[sections.length - 1];
        if (
          module.section &&
          module.section.name &&
          module.section.name !== lastSection
        ) {
          sections.push(module.section.name);
        }
      });
      dispatch({
        type: "SET_SECTIONS_ARRAY",
        payload: sections,
      });
    }
  };

  const selectAndSaveFlow = () => {
    dispatch({
      type: "SET_CURRENT_FLOW",
      payload: selectFlow(),
    });
  };

  const selectFlow = () => {
    const flowCollection = getFlowCollection(
      processState,
      authState,
      location.pathname
    );
    for (let i = 0; i < flowCollection.length; i += 1) {
      if (
        flowCollection[i].condition &&
        flowCollection[i].condition(processState, authState)
      ) {
        return flowCollection[i];
      }
    }
    return null;
  };

  const showOKMessageByPreviousModuleObjIfNeeded = () => {
    if (currentModuleObj && currentFlow) {
      const lastFlowItemOKModuleObj = currentFlow.modules[lastFlowItemOK];
      if (lastFlowItemOKModuleObj?.onOK?.showOKMessage) {
        dispatch({
          type: "SET_FLOW_MESSAGE",
          payload: {
            id: lastFlowItemOKModuleObj.onOK.showOKMessage.id,
            fadeIn: lastFlowItemOKModuleObj.onOK.showOKMessage.fadeIn,
          },
        });
      }
    }
  };

  const { dispatchEvent } = useEventDispatcher();

  const sendOnOKEventsByPreviousModuleObjIfNeeded = () => {
    if (currentModuleObj && currentFlow) {
      const lastFlowItemOKModuleObj = currentFlow.modules[lastFlowItemOK];

      if (lastFlowItemOKModuleObj?.onOK?.sendEvents) {
        lastFlowItemOKModuleObj.onOK.sendEvents.forEach((event) => {
          dispatchEvent({ ...event });
        });
      }
    }
  };

  const resetFlowMessage = () => {
    dispatch({
      type: "SET_FLOW_LOADING",
      payload: { show: false },
    });
  };

  const resetScreenPath = () => {
    dispatch({
      type: "SET_SCREEN_PATH",
      payload: undefined,
    });
  };

  const changeSectionByModuleObjIfNeeded = () => {
    if (currentModuleObj && currentModuleObj.section) {
      if (currentSection !== currentModuleObj.section.name) {
        dispatch({
          type: "UPDATE_PROCESS_CONTEXT",
          payload: {
            flowLoading: { show: false },
          },
        });
        dispatch({
          type: "SET_CURRENT_SECTION",
          payload: currentModuleObj.section.name,
        });
      }
      if (
        currentSectionProgress !== currentModuleObj.section.progressPercentage
      ) {
        dispatch({
          type: "SET_CURRENT_SECTION_PROGRESS",
          payload: currentModuleObj.section.progressPercentage,
        });
      }
    }
  };

  return (
    <Fragment>
      <AppHeader />
      <ResponsiveContainer adjustHeightToScreen>
        <FlowLoadingWindowManager />
        <FlowMessagesWindowManager />
        {conditionsToBeInitialModuleInFlowChecked && appStarted && (
          <Fragment>
            <HeaderButtons />
            <Box
              display="flex"
              flexDirection="column"
              style={{ flex: 1, minHeight: "100%", width: "100%" }}
            >
              <Box
                display="flex"
                flexDirection="column"
                justifyContent="space-between"
                style={{ flex: 1, minHeight: 0 }}
              >
                <ModuleRenderer
                  moduleId={currentModuleObj?.id}
                  hasKO={currentModuleObj?.onKO}
                  onOKOnlyResetKO={
                    currentModuleObj?.onOK === oKBehaviours.REPEAT
                  }
                  moduleProps={currentModuleObj?.props}
                />
              </Box>
              <CaixaBankCardFooter />
            </Box>
          </Fragment>
        )}
      </ResponsiveContainer>
    </Fragment>
  );
}

export default React.memo(FluxCapacitor);
